import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import EmployeeReports from './EmployeeReports';
import EmployeeSuggest from './EmployeeSuggest';

export default function AdminReports({ user }) {
  const [forms, setForms] = useState([]);
  const [lookupDatasets, setLookupDatasets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [adminReports, setAdminReports] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sourceType: 'form',
    formId: '',
    lookupDatasetId: '',
    columns: [],
    filters: [],
    recipients: []
  });
  const [selectedFormFields, setSelectedFormFields] = useState([]);

  useEffect(() => {
    fetchForms();
    fetchLookupDatasets();
    fetchDepartments();
    fetchAdminReports();
  }, []);

  const fetchForms = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/forms`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setForms(await res.json());
    } catch (e) {}
  };

  const fetchLookupDatasets = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/lookup/datasets`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setLookupDatasets(await res.json());
    } catch (e) {}
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/departments`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setDepartments(await res.json());
    } catch (e) {}
  };

  const fetchAdminReports = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/reports/admin`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setAdminReports(await res.json());
    } catch (e) {}
  };

  const dedupeFields = (fields) => {
    const seen = new Set();
    return fields.filter((f) => {
      if (seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    });
  };

  const selectSourceType = (sourceType) => {
    setSelectedFormFields([]);
    setFormData(prev => ({
      ...prev,
      sourceType,
      formId: '',
      lookupDatasetId: '',
      columns: [],
      filters: []
    }));
  };

  const handleFormSelect = (formId, useDefaults = true) => {
    const form = forms.find(f => f.id.toString() === formId.toString());
    let fields = [];
    if (form) {
      try {
        const parsed = typeof form.fields === 'string' ? JSON.parse(form.fields) : form.fields;
        fields = Array.isArray(parsed) ? parsed.filter(f => f && f.type !== 'section') : [];
      } catch (e) {}
    }
    const extras = [
      { id: 'createdAt', label: 'Date Created', type: 'date' },
      { id: 'updatedAt', label: 'Date Complete/Approval', type: 'date' },
      { id: 'initiatorName', label: 'Initiator Name', type: 'text' }
    ];
    setSelectedFormFields(dedupeFields([...fields, ...extras]));
    setFormData(prev => ({
      ...prev,
      sourceType: 'form',
      formId,
      lookupDatasetId: '',
      columns: useDefaults ? ['createdAt', 'updatedAt'] : prev.columns,
      filters: useDefaults ? ['createdAt', 'updatedAt'] : prev.filters
    }));
  };

  const handleDatasetSelect = (datasetId, useDefaults = true) => {
    const dataset = lookupDatasets.find(d => d.id === datasetId);
    let fields = [];
    if (dataset) {
      const keys = [...new Set([dataset.valueField, ...(dataset.searchFields || []), ...((dataset.metadataSchema || []).map(m => m.key))].filter(Boolean))];
      fields = keys.map(key => {
        const meta = (dataset.metadataSchema || []).find(m => m.key === key);
        return { id: key, label: meta?.label || key, type: meta?.format === 'date' ? 'date' : 'text' };
      });
    }
    const extras = [
      { id: 'createdAt', label: 'Date Created', type: 'date' },
      { id: 'updatedAt', label: 'Date Updated', type: 'date' }
    ];
    setSelectedFormFields(dedupeFields([...fields, ...extras]));
    setFormData(prev => ({
      ...prev,
      sourceType: 'lookup',
      lookupDatasetId: datasetId,
      formId: '',
      columns: useDefaults ? ['createdAt', 'updatedAt'] : prev.columns,
      filters: useDefaults ? ['createdAt', 'updatedAt'] : prev.filters
    }));
  };

  const toggleColumn = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.includes(fieldId) 
        ? prev.columns.filter(c => c !== fieldId)
        : [...prev.columns, fieldId]
    }));
  };

  const toggleFilter = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      filters: prev.filters.includes(fieldId)
        ? prev.filters.filter(c => c !== fieldId)
        : [...prev.filters, fieldId]
    }));
  };

  const toggleDepartment = (departmentName) => {
    setFormData(prev => {
      const exists = prev.recipients.some(r => r.type === 'department' && r.departmentName === departmentName);
      return {
        ...prev,
        recipients: exists
          ? prev.recipients.filter(r => !(r.type === 'department' && r.departmentName === departmentName))
          : [...prev.recipients, { type: 'department', departmentName }]
      };
    });
  };

  const addEmployeeRecipient = (employee) => {
    if (!employee || !employee.Empcode) return;
    setFormData(prev => {
      if (prev.recipients.some(r => r.type === 'employee' && r.employeeCode === employee.Empcode)) return prev;
      return {
        ...prev,
        recipients: [...prev.recipients, { type: 'employee', employeeCode: employee.Empcode, employeeName: employee.EmployeeName }]
      };
    });
  };

  const removeEmployeeRecipient = (employeeCode) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => !(r.type === 'employee' && r.employeeCode === employeeCode))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        name: formData.name,
        description: formData.description,
        formId: formData.sourceType === 'form' ? formData.formId : null,
        lookupDatasetId: formData.sourceType === 'lookup' ? formData.lookupDatasetId : null,
        columnsConfig: formData.columns,
        filtersConfig: formData.filters,
        recipients: formData.recipients
      };

      const url = editingReportId ? `${API_BASE_URL}/api/reports/${editingReportId}` : `${API_BASE_URL}/api/reports`;
      const method = editingReportId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setIsCreating(false);
        setEditingReportId(null);
        setFormData({ name: '', description: '', sourceType: 'form', formId: '', lookupDatasetId: '', columns: [], filters: [], recipients: [] });
        fetchAdminReports();
        setRefreshKey(prev => prev + 1);
      }
    } catch (e) {}
  };

  const startCreate = () => {
    setEditingReportId(null);
    setFormData({ name: '', description: '', sourceType: 'form', formId: '', lookupDatasetId: '', columns: [], filters: [], recipients: [] });
    setSelectedFormFields([]);
    setIsCreating(true);
  };

  const startEdit = async (reportId) => {
    try {
      if (!forms || forms.length === 0) {
        await fetchForms();
      }
      if (!lookupDatasets || lookupDatasets.length === 0) {
        await fetchLookupDatasets();
      }
      const res = await fetch(`${API_BASE_URL}/api/reports/${reportId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const report = data.report;
      let columns = [];
      let filters = [];
      try {
        columns = typeof report.columnsConfig === 'string' ? JSON.parse(report.columnsConfig) : report.columnsConfig;
        filters = typeof report.filtersConfig === 'string' ? JSON.parse(report.filtersConfig) : report.filtersConfig;
      } catch (e) {}

      const sourceType = report.sourceType === 'lookup' ? 'lookup' : 'form';
      if (sourceType === 'lookup') {
        handleDatasetSelect(report.lookupDatasetId, false);
      } else {
        handleFormSelect(report.formId, false);
      }
      setEditingReportId(reportId);
      setIsCreating(true);
      setFormData(prev => ({
        ...prev,
        name: report.name,
        description: report.description || '',
        sourceType,
        formId: sourceType === 'form' ? report.formId : '',
        lookupDatasetId: sourceType === 'lookup' ? report.lookupDatasetId : '',
        columns,
        filters,
        recipients: (data.recipients || []).map(r => ({
          type: r.type,
          departmentName: r.departmentName,
          employeeCode: r.employeeCode,
          employeeName: r.employeeName
        }))
      }));
    } catch (e) {}
  };

  const deleteReport = async (reportId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/reports/${reportId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        fetchAdminReports();
        setRefreshKey(prev => prev + 1);
      }
    } catch (e) {}
  };

  if (isCreating) {
    return (
      <div className="reports-shell">
        <div className="reports-header">
          <div className="reports-heading">{editingReportId ? 'Edit Report' : 'Create New Report'}</div>
          <div className="reports-sub">Design what to show, who can see it, and how they slice it.</div>
        </div>
        <form className="reports-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-field">
              <label>Report Name</label>
              <input
                required
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div className="form-field full-span">
              <label>Data Source</label>
              <div className="pill-choices">
                <button
                  type="button"
                  className={formData.sourceType === 'form' ? 'pill active' : 'pill'}
                  onClick={() => selectSourceType('form')}
                >
                  Approved Form Submissions
                </button>
                <button
                  type="button"
                  className={formData.sourceType === 'lookup' ? 'pill active' : 'pill'}
                  onClick={() => selectSourceType('lookup')}
                >
                  Lookup Dataset
                </button>
              </div>
            </div>

            {formData.sourceType === 'form' ? (
              <div className="form-field">
                <label>Form</label>
                <select
                  required
                  value={formData.formId}
                  onChange={e => handleFormSelect(e.target.value)}
                >
                  <option value="">Select a form...</option>
                  {forms.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-field">
                <label>Lookup Dataset</label>
                <select
                  required
                  value={formData.lookupDatasetId}
                  onChange={e => handleDatasetSelect(e.target.value)}
                >
                  <option value="">Select a dataset...</option>
                  {lookupDatasets.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {(formData.formId || formData.lookupDatasetId) && (
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Columns to Display</label>
                <div className="pill-choices">
                  {selectedFormFields.map(f => (
                    <button
                      key={`col-${f.id}`}
                      type="button"
                      className={formData.columns.includes(f.id) ? 'pill active' : 'pill'}
                      onClick={() => toggleColumn(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <label>Available Filters</label>
                <div className="pill-choices">
                  {selectedFormFields.map(f => (
                    <button
                      key={`filter-${f.id}`}
                      type="button"
                      className={formData.filters.includes(f.id) ? 'pill active' : 'pill'}
                      onClick={() => toggleFilter(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-field full-span">
                <label>Assign by Department</label>
                <div className="pill-choices">
                  {departments.map(d => (
                    <button
                      key={`dept-${d}`}
                      type="button"
                      className={formData.recipients.some(r => r.type === 'department' && r.departmentName === d) ? 'pill active' : 'pill'}
                      onClick={() => toggleDepartment(d)}
                    >
                      {d}
                    </button>
                  ))}
                  {departments.length === 0 && <div className="muted">No departments available.</div>}
                </div>
              </div>

              <div className="form-field full-span">
                <label>Assign to Specific Employees</label>
                <EmployeeSuggest
                  value=""
                  placeholder="Search employees by name or code..."
                  onChange={(employee) => addEmployeeRecipient(employee)}
                />
                <div className="assign-list">
                  {formData.recipients.filter(r => r.type === 'employee').map(r => (
                    <label key={`emp-${r.employeeCode}`} className="assign-row">
                      <span>{r.employeeName} ({r.employeeCode})</span>
                      <button type="button" className="button-outline" onClick={() => removeEmployeeRecipient(r.employeeCode)}>Remove</button>
                    </label>
                  ))}
                  {formData.recipients.filter(r => r.type === 'employee').length === 0 && (
                    <div className="muted">No individual employees assigned.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="actions">
            <button type="submit" className="button-primary">Save Report</button>
            <button type="button" className="button-outline" onClick={() => setIsCreating(false)}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="reports-shell">
      <div className="reports-topbar">
        <div>
          <div className="reports-heading">Reports</div>
          <div className="reports-sub">Curate datasets, filters, and viewers.</div>
        </div>
        <button className="button-primary" onClick={startCreate}>Create Report</button>
      </div>

      <div className="reports-section-title">Managed Reports</div>
      <div className="reports-grid">
        {adminReports.map(r => (
          <div key={r.id} className="report-card">
            <div className="report-card-body">
              <div className="report-card-head">
                <div className="report-card-badge">{(r.name || '?').charAt(0).toUpperCase()}</div>
                <div>
                  <div className="report-title">{r.name}</div>
                  <div className="report-meta">{r.description || 'No description'}</div>
                </div>
              </div>
            </div>
            <div className="report-card-actions">
              <button className="button-outline" onClick={() => startEdit(r.id)}>Edit</button>
              <button className="button-outline" onClick={() => deleteReport(r.id)}>Delete</button>
            </div>
          </div>
        ))}
        {adminReports.length === 0 && <div className="empty-reports">No reports created yet.</div>}
      </div>

      <EmployeeReports key={refreshKey} user={user} />
    </div>
  );
}
